import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnInit,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { finalize, tap } from 'rxjs';
import { BaseComponent } from '~app/app/~shared/abstract-components/base.component';
import { SharedModule } from '~app/app/~shared/modules/shared.module';
import { NgSelectSearchService } from '~app/app/~shared/services/ng-select-search.service';
import { TagService } from '~app/app/~shared/services/tag.service';
import { ResourceFacade } from '~app/app/~store/facades/resource.facade';
import { Tag, TagType } from '~core/models/tag.models';
import { AdvertiserSubSectorCreationDialogComponent } from '../../modals/advertiser-sub-sector-creation-dialog/advertiser-sub-sector-creation-dialog.component';

@Component({
  selector: 'advertiser-sub-sector-creation-form',
  templateUrl: './advertiser-sub-sector-creation-form.component.html',
  styleUrl: './advertiser-sub-sector-creation-form.component.scss',
  standalone: true,
  imports: [SharedModule]
})
export class AdvertiserSubSectorCreationFormComponent
  extends BaseComponent
  implements OnInit
{
  private _tagService = inject(TagService);
  private _resourceFacade = inject(ResourceFacade);
  private cdr = inject(ChangeDetectorRef);
  private _dialogRef = inject(
    MatDialogRef<AdvertiserSubSectorCreationDialogComponent>
  );

  private ngSelectSearchService = inject(NgSelectSearchService);
  private readonly pageSize = 10;
  sectorPagination = this.ngSelectSearchService.create<Tag>(this.cdr);

  private _sectorService = inject(TagService);

  subSector = input<Tag>();

  violations$ = this._globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation =>
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            })
          );
        }, 500);
      }
    })
  );

  isSubmitting = signal(false);

  form = new FormGroup({
    name: new FormControl<string>('', {
      validators: [Validators.required, Validators.min(3)]
    }),
    parentId: new FormControl('', { validators: [Validators.required] })
  });

  ngOnInit(): void {
    this.form.patchValue({
      name: this.subSector()?.name,
      parentId: this.subSector()?.parent?.id
    });
    this.sectorWireSearch();
  }

  sectorWireSearch(): void {
    this.sectorPagination
      .initSearch(params => this._sectorService.list(params), {
        itemPerPage: this.pageSize,
        type: TagType.SECTOR
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();

    this.sectorPagination.search('');
  }

  onSectorScroll(): void {
    this.sectorPagination
      .loadMore(params =>
        this._sectorService.list({
          ...params,
          itemPerPage: this.pageSize,
          type: TagType.SECTOR
        })
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  onSubmit(): void {
    this.isSubmitting.set(true);
    const subSectorId = this.subSector()?.id;

    if (subSectorId) {
      this.updateAdvertiserSubSector(subSectorId);
    } else {
      this.createAdvertiserSubSector();
    }
  }

  createAdvertiserSubSector(): void {
    const formValue = this.form.value;

    this._tagService
      .create({
        name: formValue.name as string,
        type: TagType.SUB_SECTOR,
        parentId: formValue.parentId as string
      })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Sous secteur créé avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  updateAdvertiserSubSector(subSectorId: string): void {
    const formValue = this.form.value;

    this._tagService
      .update(subSectorId, {
        name: formValue.name as string,
        type: TagType.SUB_SECTOR,
        parentId: formValue.parentId as string
      })
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Sous secteur modifié avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }
}
